import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { config } from '../config';
import { useNotification } from '../components/Notification';
import './Login.css';

function validatePassword(password) {
  if (!password || typeof password !== 'string') {
    return {
      valid: false,
      error: 'Passwort ist erforderlich'
    };
  }

  const minLength = 8;
  const hasUpperCase = /[A-Z]/.test(password);
  const hasLowerCase = /[a-z]/.test(password);
  const hasNumber = /[0-9]/.test(password);
  const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);

  const errors = [];

  if (password.length < minLength) {
    errors.push(`Mindestens ${minLength} Zeichen`);
  }

  if (!hasUpperCase) {
    errors.push('Mindestens ein Großbuchstabe');
  }

  if (!hasLowerCase) {
    errors.push('Mindestens ein Kleinbuchstabe');
  }

  if (!hasNumber) {
    errors.push('Mindestens eine Zahl');
  }

  if (!hasSpecialChar) {
    errors.push('Mindestens ein Sonderzeichen');
  }

  if (errors.length > 0) {
    return {
      valid: false,
      error: `Passwort erfüllt nicht die Anforderungen: ${errors.join(', ')}`
    };
  }

  return {
    valid: true
  };
}

function CustomerRegister() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showNotification } = useNotification();
  const tenantId = searchParams.get('tenantId') || '';
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      showNotification('Passwörter stimmen nicht überein', 'error');
      return;
    }

    const passwordValidation = validatePassword(formData.password);
    if (!passwordValidation.valid) {
      showNotification(passwordValidation.error || 'Passwort erfüllt nicht die Anforderungen', 'error');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${config.API_URL || '/api'}/customer-auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          password: formData.password,
          phone: formData.phone,
          tenantId: tenantId || undefined
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Registrierung fehlgeschlagen');
      }

      const successMessage = data.emailVerified 
        ? 'Registrierung erfolgreich!' 
        : 'Registrierung erfolgreich! Bitte bestätigen Sie Ihre E-Mail-Adresse.';
      showNotification(successMessage, 'success');
      
      const loginUrl = tenantId 
        ? `/customer-login?tenantId=${tenantId}&email=${encodeURIComponent(formData.email)}`
        : `/customer-login?email=${encodeURIComponent(formData.email)}`;
      navigate(loginUrl);
    } catch (error) {
      console.error('Registration error:', error);
      showNotification(error.message || 'Fehler bei der Registrierung', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-box">
        <h1>Kundenregistrierung</h1>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Vorname</label>
            <input
              type="text"
              value={formData.firstName}
              onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
              required
            />
          </div>
          <div className="form-group">
            <label>Nachname</label>
            <input
              type="text"
              value={formData.lastName}
              onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
              required
            />
          </div>
          <div className="form-group">
            <label>E-Mail</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
            />
          </div>
          <div className="form-group">
            <label>Telefon (optional)</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>Passwort</label>
            <input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required
              minLength={8}
            />
            <small style={{ color: '#666', fontSize: '0.85rem', display: 'block', marginTop: '0.25rem' }}>
              Mindestens 8 Zeichen, Großbuchstabe, Kleinbuchstabe, Zahl und Sonderzeichen
            </small>
          </div>
          <div className="form-group">
            <label>Passwort bestätigen</label>
            <input
              type="password"
              value={formData.confirmPassword}
              onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
              required
              minLength={8}
            />
          </div>
          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? 'Registrierung...' : 'Registrieren'}
          </button>
        </form>
        <div style={{ marginTop: '1.5rem', textAlign: 'center' }}>
          <p style={{ marginBottom: '0.5rem' }}>
            Bereits registriert?{' '}
            <Link 
              to={tenantId ? `/customer-login?tenantId=${tenantId}` : '/customer-login'}
              style={{ color: '#2196f3', fontWeight: '600', textDecoration: 'none' }}
            >
              Hier anmelden
            </Link>
          </p>
          <p style={{ marginTop: '1rem', fontSize: '0.875rem', color: '#666' }}>
            Oder{' '}
            <Link 
              to="/customer-login?email=testkunde@example.com&password=test123"
              style={{ color: '#2196f3', fontWeight: '600', textDecoration: 'none' }}
            >
              Demo-Konto verwenden
            </Link>
          </p>
        </div>
      </div>
      
      <footer className="login-footer">
        <div className="login-footer-content">
          <Link 
            to="/customer-login?email=testkunde@example.com&password=test123"
            className="login-footer-link demo-link"
            style={{ fontWeight: '600', color: '#2196f3' }}
          >
            Kunde Demo
          </Link>
          <Link 
            to="/login?demo=true"
            className="login-footer-link demo-link"
            style={{ fontWeight: '600', color: '#2196f3' }}
          >
            Werkstatt Demo
          </Link>
          <Link to="/customer-login" className="login-footer-link">
            Login
          </Link>
          <Link to="/support" className="login-footer-link">
            Support
          </Link>
          <Link to="/impressum" className="login-footer-link">
            Impressum
          </Link>
          <Link to="/datenschutz" className="login-footer-link">
            Datenschutz
          </Link>
        </div>
      </footer>
    </div>
  );
}

export default CustomerRegister;
